import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './JackpotThumbnail.css';
import { RollNumber } from '../../../../../shared/src/components/RollNumber';
import { useTranslations } from '../../../../../shared/src/hooks/use-translations.hook';
import NumberBoxSeparator from '../../../../../shared/src/components/NumberBoxSeparator';
import { GamesService } from '../../../../../shared/src/services/games.service';
import { StringUtilService } from '../../../../../shared/src/services/util/string-util.service';
import { PerfUtilService } from '../../../../../shared/src/services/util/perf-util.service';
import { JackpotsService } from '../../../../../shared/src/services/jackpots.service';
import { ForceUpdateContext } from '../../../../../shared/src/contexts';
import { useAsgActions } from '../../../../../shared/src/hooks/use-asg-actions.hook';

const JackpotThumbnail = ({ mode = 'game', jackpot }) => {
    const [overlayActive, setOverlayActive] = useState(false);
    const [thumbnailSize, setThumbnailSize] = useState('small');
    const [jackpotId, setJackpotId] = useState(0);
    const { isMobile, verticals } = useSelector((state) => state.app);
    const { balance } = useSelector((state) => state.player);
    const { gamesToJackpots } = useSelector((state) => state.jackpots);
    const { gamesById } = useSelector((state) => state.casino);
    const dispatch = useDispatch();
    const { loadGame } = useAsgActions();

    const openGame = useCallback(() => {
        const gameId = jackpot?.thumbnailGameId;
        const jackpotGameIds = Object.keys(gamesToJackpots);
        const availableGameIds = Object.keys(gamesById);

        if (!gameId) {
            Tools.error('Game ID is not set');
            return;
        } else if (!jackpotGameIds.includes(gameId) && !availableGameIds.includes(gameId)) {
            Tools.error('Wrong game ID or game is not available', gameId);
            return;
        }

        return dispatch(loadGame({
            gameId,
        }));
    }, [jackpot, gamesToJackpots, gamesById]);

    let amount = jackpot?.amount[balance?.Currency];
    if (!amount) {
        return null;
    }

    const openJackpotVertical = () => JackpotsService.applyDeepLink({
        verticals,
        dispatch,
        jackpotId,
        forceUpdateVerticals
    });
    const onClickHandler = jackpot?.thumbnailBehavior === 'openGame' ? openGame : openJackpotVertical;

    const getThumbnailEventHandlers = () => {
        let eventHandlers = {
            onClick: onClickHandler,
        };

        // show and hide overlay on hover for desktop
        if (!isMobile) {
            eventHandlers = {
                onMouseEnter: () => { setOverlayActive(true);},
                onMouseLeave: () => { setOverlayActive(false);},
            };
        }

        return eventHandlers;
    };

    const forceUpdateVerticals = useContext(ForceUpdateContext);
    const decodeCurrency = StringUtilService.decodeHtmlEntity(balance?.CurrencySign);
    amount = amount.toFixed().toString().split('');
    const { t } = useTranslations();
    const thumbnailRef = useRef();

    useEffect(() => {
        const calculateItemSize = () => {
            const { getThumbnailSizeClass, getBannerSizeClass } = GamesService;
            if (thumbnailRef && thumbnailRef.current) {
                const { width, height } = thumbnailRef?.current?.getBoundingClientRect() || {};
                const currentWidth = (mode === 'game')
                    ? getThumbnailSizeClass(width, height)
                    : getBannerSizeClass(height);

                setThumbnailSize(currentWidth);
            }
        };

        calculateItemSize();

        const debouncedSizeCalculation = PerfUtilService.debounce(calculateItemSize, 50);
        window.addEventListener('resize', debouncedSizeCalculation);

        return () => {
            window.removeEventListener('resize', debouncedSizeCalculation);
        };
    }, []);

    useEffect(() => {
        setJackpotId(jackpot?.jackpotId);
    }, [jackpot]);

    const colors = {
        counterText: jackpot?.colors?.counterText,
        numbersBottomGradient: jackpot?.colors?.numbersBottomGradient,
        numbersBoxBottomGradient: jackpot?.colors?.numbersBoxBottomGradient,
        numbersBoxTopGradient: jackpot?.colors?.numbersBoxTopGradient,
        numbersTopGradient: jackpot?.colors?.numbersTopGradient,
    };

    const blockStyle = {
        '--asg-jp-jackpot-thumbnail-background-color':
            `linear-gradient(180deg, ${colors?.numbersBoxTopGradient} 0%, ${colors?.numbersBoxBottomGradient} 100%)`,
        '--asg-jp-jackpot-thumbnail-border-color': colors?.counterText
    };

    const style = {
        '--asg-jp-jackpot-thumbnail-background-image':
            `linear-gradient(180deg, ${colors?.numbersTopGradient} 0%, ${colors?.numbersBottomGradient} 100%)`
    };

    const separatorStyle = {
        '--asg-jp-separator-background-image':
            `linear-gradient(180deg, ${colors?.numbersBoxTopGradient} 0%, ${colors?.numbersBoxBottomGradient} 100%)`
    };

    const overlayTextClasses = [
        'casino-thumbnail__play-text',
        `casino-thumbnail__play-text--${mode}`,
        `casino-thumbnail__play-text--${thumbnailSize}`,
    ];

    const wrapperClasses = [
        `jackpot-thumbnail-wrapper--${mode}`,
        `jackpot-thumbnail-wrapper--${thumbnailSize}`,
        jackpot.expiresIn ? 'jackpot-thumbnail-wrapper--with-date' : '',
    ];

    return (
        <div
            className={`jackpot-thumbnail-container jackpot-thumbnail-container--${mode}`}
            ref={thumbnailRef}
            {...getThumbnailEventHandlers()}
        >
            <img className="jackpot-thumbnail-background" src={jackpot.images.background} alt="Background" />

            {jackpot &&
                <div className={wrapperClasses.join(' ')}>
                    <div
                        className={`jackpot-thumbnail-logo__container--${mode} 
                        jackpot-thumbnail-logo__container--${thumbnailSize}`}
                    >
                        <img
                            src={jackpot.images.logo}
                            alt="Logo"
                            className={`jackpot-thumbnail-logo--${mode} jackpot-thumbnail-logo--${thumbnailSize}`}
                        />
                    </div>

                    <div className={`jackpot-thumbnail-amount__container--${thumbnailSize}`}>
                        <div
                            className={`jackpot-thumbnail-numbers jackpot-thumbnail-numbers--${thumbnailSize}`}
                            style={{ WebkitTextStrokeColor: colors?.counterText }}
                            data-jackpot-amount={jackpotId}
                        >
                            <span
                                className={`jackpot-thumbnail-currency--${thumbnailSize}`}
                                style={{
                                    background:
                                        `linear-gradient(180deg, ${colors?.numbersBoxTopGradient} 0%, 
                                        ${colors?.numbersBoxBottomGradient} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent' }}
                            >
                                {decodeCurrency}
                            </span>
                            {amount.map((number, index) =>
                                NumberBoxSeparator(RollNumber, amount, index)({
                                    digit: number, blockStyle, style,
                                    separatorStyle, size: thumbnailSize, separator: ',' }))}
                        </div>
                        {jackpot.expiresIn && <div
                            className={`jackpot-thumbnail-countdown__container--${thumbnailSize}`}
                            style={{ 'color': colors?.counterText }}
                        >
                            <div className="jackpot-thumbnail-countdown__wrapper">
                                <span className="jackpot-thumbnail-countdown__text">{t('mustBeWon')}</span>
                                <div
                                    className={`jackpot-thumbnail-countdown__timer--${thumbnailSize}`}
                                    style={{ 'background': `linear-gradient(180deg, 
                                    ${colors?.numbersBoxTopGradient} 0%, ${colors?.numbersBoxBottomGradient} 100%)` }}
                                >
                                    <div className={`jackpot-thumbnail-timer__clock--${thumbnailSize}`}></div>
                                    <div className="jackpot-thumbnail-clock__hour">
                                        {`${jackpot.expiresIn.hours}${t('stripHr')}`}
                                    </div>
                                    <div className="jackpot-thumbnail-clock__minutes">
                                        {`${jackpot.expiresIn.minutes}${t('stripMin')}`}
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </div>
                </div>}

            {overlayActive && (
                <div
                    className="jackpot-thumbnail__hover"
                    onClick={onClickHandler}
                >
                    <div
                        className={overlayTextClasses.join(' ')}>
                        <span>{t('viewGames')}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JackpotThumbnail;
